// MQTT traffic monitor — the data behind the Super Admin message console.
//
// The app normally subscribes to exactly the devices on screen. The console
// needs to see EVERYTHING, so it adds a wildcard subscription while it is open
// and drops it on the way out. Nothing else changes: the same client, the same
// connection, the same per-device subscriptions carry on underneath.
//
// SUBSCRIBES TO '#', like the reference console.
//
// An earlier version used `+/client` and `+/device`, reasoning that those are
// the only shapes devices use and a narrower filter keeps the volume down. That
// was a guess about the topic space, and a wrong guess silently DROPS messages —
// which is the one thing a diagnostic tool must never do. If a device ever
// publishes to a third shape, '#' shows it and the narrow filter would not.
//
// MEMORY: a ring buffer, newest first, capped at MAX_MESSAGES.
//
// RENDER RATE: listeners are notified on a timer, not per message. A busy broker
// delivers faster than React can re-render a list, and one setState per message
// makes the view lag behind the traffic — which reads as "messages are missing"
// even though the buffer has them all.

import {
  connectMqtt, addMessageListener, subscribeTopic, unsubscribeTopic,
} from './mqttClient';

export const MAX_MESSAGES = 500;

// Everything on the broker — see the note above on why this is not narrowed.
const WILDCARDS = ['#'];

// How often the UI is told about new messages. 120 ms keeps the list feeling
// live while collapsing a burst of twenty messages into ONE re-render instead of
// twenty.
const FLUSH_MS = 120;

let buffer = [];          // newest first
let seq = 0;
const listeners = new Set();
let detach = null;
let refCount = 0;
let flushTimer = null;
let dirty = false;

function emit() {
  dirty = false;
  listeners.forEach((fn) => { try { fn(buffer); } catch (e) { /* ignore */ } });
}

// Coalesce notifications. The first message after an idle period paints
// immediately (so a single command's reply is not delayed); a burst then flushes
// on the timer.
function notify() {
  if (flushTimer) { dirty = true; return; }
  emit();
  flushTimer = setInterval(() => {
    if (dirty) { emit(); return; }
    clearInterval(flushTimer);
    flushTimer = null;
  }, FLUSH_MS);
}

// "897242/client" -> "897242"
export function unoFromTopic(topic) {
  const t = String(topic || '');
  const slash = t.indexOf('/');
  return slash === -1 ? t : t.slice(0, slash);
}

function record(topic, payload) {
  const now = new Date();
  seq += 1;
  const entry = {
    id: seq,
    at: now,
    // HH:MM:SS, matching the console's log format.
    time: now.toTimeString().slice(0, 8),
    topic,
    uno: unoFromTopic(topic),
    // `<topic>: <payload>` is how a line reads in the reference console.
    text: `${topic}: ${payload}`,
    payload,
  };
  buffer = [entry, ...buffer];
  if (buffer.length > MAX_MESSAGES) buffer.length = MAX_MESSAGES;
  notify();
}

// Start monitoring. Ref-counted so two mounted panes share one subscription and
// the last one to leave tears it down.
export function startMonitor() {
  refCount += 1;
  if (refCount > 1) return;

  connectMqtt();
  WILDCARDS.forEach((w) => subscribeTopic(w));
  detach = addMessageListener((topic, payload) => record(topic, payload));
}

export function stopMonitor() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0) return;
  if (detach) { detach(); detach = null; }
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
  WILDCARDS.forEach((w) => unsubscribeTopic(w));
}

export function subscribeMessages(fn) {
  listeners.add(fn);
  fn(buffer);
  return () => listeners.delete(fn);
}

export function getMessages() {
  return buffer;
}

export function clearMessages() {
  buffer = [];
  notify();
}

// Log a line the console itself generated (a publish, a subscription), so the
// view shows what WE sent alongside what arrived — the reference console does
// the same and it is what makes a send/response pair readable.
export function logLocal(text) {
  record('app', text);
}
